import {isNil, isNotNil} from 'ramda';

import {metadaApi} from '@dms/api/base';

import {metadaWorkshopApi} from '../metadaWorkshop/api';
import {metadaWorkshopServiceCaseApi} from '../metadaWorkshopServiceCase/api';
import {metadaWorkshopServiceCaseActionsApi} from '../metadaWorkshopServiceCaseActions/api';
import {
  GetServiceCaseDetailBootstrapApiArg,
  GetServiceCaseDetailBootstrapApiArgSchema,
  GetServiceCaseDetailBootstrapApiResponse,
  GetServiceCaseDetailBootstrapApiResponseSchema,
} from './types';

export const metadaWorkshopServiceCaseBootstrapApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getServiceCaseDetailBootstrap: build.query<
      GetServiceCaseDetailBootstrapApiResponse,
      GetServiceCaseDetailBootstrapApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/detail-bootstrap`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'checkIn', id: queryArg.serviceCaseId},
        {type: 'serviceCaseIntergations', id: queryArg.serviceCaseId},
      ],
      async onQueryStarted(queryArg, {dispatch, getState, queryFulfilled}) {
        try {
          const {data} = await queryFulfilled;
          if (isNil(data)) {
            return;
          }
          const {serviceCaseId} = queryArg;

          dispatch(
            metadaWorkshopServiceCaseApi.util.upsertQueryData(
              'getPostponeJobs',
              {serviceCaseId},
              data.postponeJobs ?? null
            )
          );
          dispatch(
            metadaWorkshopServiceCaseActionsApi.util.upsertQueryData(
              'getServiceCaseActions',
              {serviceCaseId},
              isNotNil(data.caseActions) ? {caseActions: data.caseActions} : null
            )
          );
          dispatch(
            metadaWorkshopServiceCaseApi.util.upsertQueryData(
              'getCaseItemsTotalAmount',
              {serviceCaseId},
              data.itemsTotalAmount ?? null
            )
          );
          dispatch(
            metadaWorkshopServiceCaseApi.util.upsertQueryData(
              'getServiceCaseOrderCount',
              {serviceCaseId},
              isNotNil(data.orderCount) ? {count: data.orderCount} : null
            )
          );
          if (isNotNil(data.orderVariants)) {
            dispatch(
              metadaWorkshopServiceCaseApi.util.upsertQueryData(
                'getServiceCaseOrderVariants',
                {serviceCaseId},
                data.orderVariants
              )
            );
          }
          const existingPendingServiceItems =
            metadaWorkshopServiceCaseApi.endpoints.getPendingServiceItems.select({serviceCaseId})(
              getState()
            ).data;
          if (isNotNil(data.pendingServiceItems) || isNil(existingPendingServiceItems)) {
            dispatch(
              metadaWorkshopServiceCaseApi.util.upsertQueryData(
                'getPendingServiceItems',
                {serviceCaseId},
                isNotNil(data.pendingServiceItems)
                  ? {
                      workBasket: data.pendingServiceItems.workBasket.filter(isNotNil),
                      materialBasket: data.pendingServiceItems.materialBasket.filter(isNotNil),
                    }
                  : null
              )
            );
          }
          dispatch(
            metadaWorkshopServiceCaseApi.util.upsertQueryData(
              'getPendingServiceOrderReturn',
              {serviceCaseId},
              data.pendingServiceOrderReturn ?? null
            )
          );

          const {vehicle} = data;
          const vehicleId = data.case?.vehicleId;
          if (isNotNil(vehicleId) && isNotNil(vehicle)) {
            if (isNotNil(vehicle.activeServiceCasesCount)) {
              dispatch(
                metadaWorkshopApi.util.upsertQueryData(
                  'getServiceCaseByVehicleCount',
                  {vehicleId, status: 'ACTIVE'},
                  {count: vehicle.activeServiceCasesCount}
                )
              );
            }
            if (isNotNil(vehicle.finishedServiceCasesCount)) {
              dispatch(
                metadaWorkshopApi.util.upsertQueryData(
                  'getServiceCaseByVehicleCount',
                  {vehicleId, status: 'FINISHED'},
                  {count: vehicle.finishedServiceCasesCount}
                )
              );
            }
          }
        } catch {
          // Bootstrap failure seeds nothing — the settle gate opens and every consumer
          // falls back to its granular query (the pre-bootstrap behavior).
        }
      },
      extraOptions: {
        requestSchema: GetServiceCaseDetailBootstrapApiArgSchema,
        responseSchema: GetServiceCaseDetailBootstrapApiResponseSchema,
      },
    }),
  }),
});

export const {useGetServiceCaseDetailBootstrapQuery} = metadaWorkshopServiceCaseBootstrapApi;
